import { useFormContext } from 'react-hook-form'
import useConfig from 'modules/config/state/use-config'
import SelectField from 'modules/ui/molecules/FormComponents/SelectField'
import { OrderContactAnswersSlice } from 'modules/order/state/contact-answers/contact-answers-types'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import { OrderContactSlice } from 'modules/order/state/contact/contact-types'
import Column from 'modules/ui/atoms/Layout/Column'
import BirthdateField from 'modules/ui/molecules/FormComponents/BirthdateField'
import { useTranslation } from 'next-i18next'

const autoCompleteMap: { [key: string]: string } = {
  gender: 'gender',
  cellphone: 'tel',
  middle_name: 'additional-name',
  birthday: 'bday',
  company_name: 'organization',
  suffix: 'honorific-suffix',
  pronoun: 'honorific-prefix',
  firstName: 'given-name',
  website: 'url',
}

interface OrderContactQuestionFieldProps {
  fieldName: keyof OrderContactAnswersSlice | keyof OrderContactSlice
  showRequired: boolean
}

const OrderContactQuestionField = ({ fieldName, showRequired }: OrderContactQuestionFieldProps) => {
  const config = useConfig()
  const form = useFormContext()
  const { t } = useTranslation()

  const fieldConfig = config.questions.contact.find((question) => question.tag === fieldName)

  // Use the question tag as the form id and error key directly (no remapping)
  const idForForm = fieldName as string
  const fieldError = form.formState.errors[idForForm] as { message: string } | undefined

  if (!fieldConfig || fieldConfig.is_required !== showRequired) return null

  const autoComplete = autoCompleteMap[fieldConfig.tag as string] ?? ''

  /* TODO - i18n: Determine the appropriate translation for fieldConfig.label */
  const { label: oldLabel, tag } = fieldConfig

  let label

  switch (tag) {
    case 'prefix':
      label = t('OrderContactQuestions.prefix.label', 'Title')
      break
    case 'middle_name':
      label = t('OrderContactQuestions.middleName.label', 'Middle name')
      break
    case 'suffix':
      label = t('OrderContactQuestions.suffix.label', 'Suffix')
      break
    case 'homephone':
      label = t('OrderContactQuestions.homephone.label', 'Primary phone')
      break
    case 'cellphone':
      label = t('OrderContactQuestions.cellphone.label', 'Cell phone')
      break
    case 'gender':
      label = t('OrderContactQuestions.gender.label', 'Gender')
      break
    case 'pronoun':
      label = t('OrderContactQuestions.pronoun.label', 'Pronouns')
      break
    case 'birthdate':
      label = t('OrderContactQuestions.birthdate.label', 'Date of birth')
      break
    case 'company_name':
      label = t('OrderContactQuestions.companyName.label', 'Company / organization')
      break
    case 'website':
      label = t('OrderContactQuestions.website.label', 'Website')
      break
    default:
      label = oldLabel
      break
  }

  switch (fieldConfig.type) {
    case 'enum': {
      const EMPTY_OPTION = { label: '', value: '' }

      const options = fieldConfig.enum_options
        ? [
            EMPTY_OPTION,
            ...fieldConfig.enum_options.map((option) => ({
              label: option.label,
              value: option.label,
            })),
          ]
        : []

      return (
        <Column>
          <SelectField
            id={idForForm}
            options={options}
            label={label}
            error={fieldError}
            placeholder={t('OrderContactQuestionField.select.placeholder', 'Please select')}
            dataId={`contact-${idForForm}-select`}
            required={fieldConfig.is_required}
            autoComplete={autoComplete}
          />
        </Column>
      )
    }

    case 'date': {
      return (
        <Column>
          <BirthdateField
            id={idForForm}
            label={label}
            error={fieldError}
            required={fieldConfig.is_required}
            dataId={`contact-${idForForm}-birthday`}
          />
        </Column>
      )
    }

    default: {
      const fieldType =
        fieldConfig.tag === 'cellphone' || fieldConfig.tag === 'homephone'
          ? 'tel'
          : fieldConfig.type

      return (
        <Column>
          <InputField
            id={idForForm}
            type={fieldType}
            label={label}
            error={fieldError}
            required={fieldConfig.is_required}
            dataId={`contact-${idForForm}-input`}
            autoComplete={autoComplete}
          />
        </Column>
      )
    }
  }
}

export default OrderContactQuestionField
